import { clampRangeToMaxDays, toYmd } from '../../utils/date';
import { db } from '../../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

// Returns pairs of short event keys (e.g., ['E1','E3']) that overlap on the same date
export function computeOverlappingEventKeyPairs(events = [], eventKeyMap = {}) {
  // Invert mapping: eventId -> short key
  const idToKey = Object.entries(eventKeyMap).reduce((acc, [shortKey, eventId]) => {
    acc[eventId] = shortKey;
    return acc;
  }, {});

  // Group events by YYYY-MM-DD of start date
  const byDate = new Map();
  for (const e of events) {
    const d = new Date(e.start);
    if (Number.isNaN(d.getTime())) continue;
    const ymd = toYmd(d);
    if (!byDate.has(ymd)) byDate.set(ymd, []);
    byDate.get(ymd).push(e);
  }

  const overlaps = [];
  for (const [, list] of byDate.entries()) {
    const timeline = list
      .map((e) => ({ id: e.id, s: new Date(e.start).getTime(), e: new Date(e.end || e.start).getTime() }))
      .filter((t) => Number.isFinite(t.s) && Number.isFinite(t.e))
      .sort((a, b) => a.s - b.s);
    for (let i = 0; i < timeline.length; i++) {
      for (let j = i + 1; j < timeline.length; j++) {
        const a = timeline[i];
        const b = timeline[j];
        if (b.s >= a.e) break; // no further overlaps with i
        if (a.s < b.e && b.s < a.e) {
          const k1 = idToKey[a.id];
          const k2 = idToKey[b.id];
          if (k1 && k2) overlaps.push([k1, k2]);
        }
      }
    }
  }
  return overlaps;
}

// Build optimization input model and short-key dictionaries
// - Users: prefix 'U' (e.g., 'U1'), includes only selected participants
// - Events: prefix 'E' (e.g., 'E1'), includes only events overlapping the clamped time window
export async function buildOptimizationModel({ planningId, startDate, endDate, people = [], events = [], maxDays, weeklyDefaults }) {
  const { start, end } = clampRangeToMaxDays(new Date(startDate), new Date(endDate), maxDays);

  // Participants: preserve full list with flags and caps
  const participants = people.map((p) => ({
    uid: p.uid,
    selected: !!p.selected,
    weeklyHoursMax: Number(p.weeklyHoursMax) || 0,
  }));

  // Users in scope: only selected
  const selectedPeople = participants.filter((p) => p.selected);
  const userKeyMap = {};
  selectedPeople.forEach((p, idx) => {
    const key = `U${idx + 1}`;
    userKeyMap[key] = p.uid;
  });

  // Events in scope: event start within [start 00:00, end 23:59:59]
  const startFloor = new Date(start);
  startFloor.setHours(0, 0, 0, 0);
  const endCeil = new Date(end);
  endCeil.setHours(23, 59, 59, 999);
  const startMs = startFloor.getTime();
  const endMs = endCeil.getTime();
  const normalizedEvents = Array.isArray(events) ? events : [];
  const inScopeEvents = normalizedEvents.filter((e) => {
    const evStart = new Date(e.start).getTime();
    return Number.isFinite(evStart) && evStart >= startMs && evStart <= endMs;
  });

  const eventKeyMap = {};
  const staffRequiredMap = {};
  // Role-aware: collect roles used across events and people, and build per-event role requirements
  const roleNameSet = new Set();
  const eventDurationMap = {};
  const eventEffectiveDurationMap = {};
  const eventStartMsMap = {};
  const eventEndMsMap = {};
  const eventDurationMinutesMap = {};
  const eventEffectiveMinutesMap = {};
  const eventLocationMap = {};
  inScopeEvents.forEach((e, idx) => {
    const key = `E${idx + 1}`;
    eventKeyMap[key] = e.id;
    const sr = Number(e?.extendedProps?.staffRequired ?? e?.staffRequired) || 1;
    staffRequiredMap[key] = sr;
    // Collect roles from event rolesRequired if present
    const rolesRequiredArr = Array.isArray(e?.extendedProps?.rolesRequired)
      ? e.extendedProps.rolesRequired
      : (Array.isArray(e?.rolesRequired) ? e.rolesRequired : []);
    for (const r of rolesRequiredArr) {
      const name = String(r?.role || '').trim();
      if (name) roleNameSet.add(name);
    }
    const sMs = new Date(e.start).getTime();
    const eMs = new Date(e.end || e.start).getTime();
    const durH = Number.isFinite(sMs) && Number.isFinite(eMs) ? Math.max(0, (eMs - sMs) / 3600000) : 0;
    const durMin = Math.round(durH * 60);
    eventDurationMap[key] = durH;
    eventDurationMinutesMap[key] = durMin;
    // If event defines effectiveMinutes, prefer it; else use full duration
    // Align with UI: only use effectiveMinutes when strictly > 0; otherwise fallback to full duration
    const effMin = Number(e?.effectiveMinutes ?? e?.extendedProps?.effectiveMinutes);
    const effH = Number.isFinite(effMin) && effMin > 0 ? Math.min(durH, effMin / 60) : durH;
    const effMinutes = Math.round(effH * 60);
    eventEffectiveDurationMap[key] = effH;
    eventEffectiveMinutesMap[key] = effMinutes;
    if (Number.isFinite(sMs)) eventStartMsMap[key] = sMs;
    if (Number.isFinite(eMs)) eventEndMsMap[key] = eMs;
    const loc = String(e?.extendedProps?.location ?? e?.location ?? '').trim().toLowerCase();
    eventLocationMap[key] = loc;
  });

  // Load roles and contract from members subcollection to determine eligibility (uid -> [role]) and employment
  const rolesByUser = new Map();
  const contractByUser = new Map(); // uid -> 'internal' | 'external'
  if (planningId && selectedPeople.length) {
    try {
      const membersCol = collection(db, 'plannings', planningId, 'members');
      const snap = await getDocs(membersCol);
      for (const d of snap.docs) {
        const data = d.data() || {};
        const roles = Array.isArray(data.roles)
          ? data.roles.filter((r) => typeof r === 'string' && r.trim()).map((r) => r.trim())
          : [];
        if (roles.length) rolesByUser.set(d.id, roles);
        for (const r of roles) roleNameSet.add(String(r));
        const contract = (data.contract === 'external' || data.contract === 'internal') ? data.contract : 'internal';
        contractByUser.set(d.id, contract);
      }
    } catch (e) {
      // ignore errors; eligibility falls back below
    }
  }

  // Collect roles from people as well (for eligibility universe)
  for (const p of Array.isArray(people) ? people : []) {
    const roles = Array.isArray(p?.roles) ? p.roles : [];
    for (const r of roles) {
      const name = String(r || '').trim();
      if (name) roleNameSet.add(name);
    }
  }

  // Fallback role if none defined anywhere but staffRequired exists
  const usingDefaultRole = roleNameSet.size === 0;
  if (usingDefaultRole) roleNameSet.add('default');

  // Build role key map R# -> roleName and inverse
  const roleKeyMap = {};
  const roleNameToKey = {};
  Array.from(roleNameSet).forEach((name, idx) => {
    const key = `R${idx + 1}`;
    roleKeyMap[key] = name;
    roleNameToKey[name] = key;
  });

  // Compute overlap edges (objects) to avoid nested arrays in Firestore
  const overlapPairs = computeOverlappingEventKeyPairs(inScopeEvents, eventKeyMap);
  const overlapsEdges = overlapPairs.map(([a, b]) => (a < b ? { a, b } : { a: b, b: a }));


  // Build list of day keys in scope (inclusive)
  const dayKeys = [];
  {
    const pad = (n) => String(n).padStart(2, '0');
    const d0 = new Date(start);
    const d1 = new Date(end);
    d0.setHours(0, 0, 0, 0);
    d1.setHours(0, 0, 0, 0);
    for (let d = new Date(d0); d <= d1; d.setDate(d.getDate() + 1)) {
      const ymd = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      dayKeys.push(ymd);
    }
  }

  // Group events by week (keyed by the Monday date YYYY-MM-DD)
  const idToEventKey = Object.entries(eventKeyMap).reduce((acc, [ekey, eid]) => {
    acc[eid] = ekey;
    return acc;
  }, {});
  // Also track event date (YYYY-MM-DD) for daily constraints
  const eventDateYmd = {};
  const eventInSameWeek = {};
  const weekKeyFromDate = (d) => {
    const date = new Date(d);
    const day = date.getDay(); // 0=Sun..6=Sat
    const diffSinceMonday = (day + 6) % 7; // days since Monday
    const monday = new Date(date);
    monday.setDate(date.getDate() - diffSinceMonday);
    monday.setHours(0, 0, 0, 0);
    return toYmd(monday);
  };
  for (const e of inScopeEvents) {
    const key = idToEventKey[e.id];
    if (!key) continue;
    // Store event start date in YYYY-MM-DD
    try {
      eventDateYmd[key] = toYmd(new Date(e.start));
    } catch {}
    const wk = weekKeyFromDate(e.start);
    if (!eventInSameWeek[wk]) eventInSameWeek[wk] = [];
    eventInSameWeek[wk].push(key);
  }

  // Build linked event edges: only consider links within the same day
  const linkedEdges = [];
  {
    const seen = new Set();
    // Map from id to event object for quick lookup
    const byId = new Map(inScopeEvents.map((ev) => [ev.id, ev]));
    for (const ev of inScopeEvents) {
      const aKey = idToEventKey[ev.id];
      if (!aKey) continue;
      const links = Array.isArray(ev?.extendedProps?.linkedEventIds)
        ? ev.extendedProps.linkedEventIds
        : (Array.isArray(ev?.linkedEventIds) ? ev.linkedEventIds : []);
      for (const bId of links) {
        const bEv = byId.get(bId);
        const bKey = idToEventKey[bId];
        if (!bEv || !bKey) continue;
        // same-day check using computed YMD
        if ((eventDateYmd[aKey] || '') !== (eventDateYmd[bKey] || '')) continue;
        const a = aKey < bKey ? aKey : bKey;
        const b = aKey < bKey ? bKey : aKey;
        const tag = `${a}|${b}`;
        if (seen.has(tag)) continue;
        seen.add(tag);
        linkedEdges.push({ a, b });
      }
    }
  }

  // Build same-day adjacency edges (consecutive events by start time)
  // Each edge captures a pair (a -> b) within the same day and the gap in ms
  const adjacencyEdges = [];
  {
    // Group events by day ymd
    const byDay = new Map(); // ymd -> [{ eKey, s, e }]
    for (const [eKey, ymd] of Object.entries(eventDateYmd)) {
      const s = eventStartMsMap[eKey];
      const e = eventEndMsMap[eKey];
      if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
      if (!byDay.has(ymd)) byDay.set(ymd, []);
      byDay.get(ymd).push({ eKey, s, e });
    }
    for (const [day, itemsRaw] of byDay.entries()) {
      const items = itemsRaw.slice().sort((a, b) => a.s - b.s);
      for (let i = 0; i + 1 < items.length; i++) {
        const a = items[i];
        const b = items[i + 1];
        const gapMs = Number.isFinite(a.e) && Number.isFinite(b.s) ? (b.s - a.e) : 0;
        adjacencyEdges.push({ day, a: a.eKey, b: b.eKey, gapMs });
      }
    }
  }

  // Build user unavailability map from Firestore: U# -> [E#]
  const userUnavailable = {};
  if (planningId && selectedPeople.length && inScopeEvents.length) {
    const inScopeIdSet = new Set(inScopeEvents.map((ev) => ev.id));
    const availCol = collection(db, 'plannings', planningId, 'availabilities');
    for (const [userKey, uid] of Object.entries(userKeyMap)) {
      const q = query(availCol, where('userId', '==', uid), where('available', '==', false));
      const snap = await getDocs(q);
      const list = [];
      for (const d of snap.docs) {
        const eid = d.get('eventId');
        if (inScopeIdSet.has(eid)) {
          const eKey = idToEventKey[eid];
          if (eKey) list.push(eKey);
        }
      }
      userUnavailable[userKey] = list;
    }
  }

  // Build per-event role requirements: staffRequiredByRole[E#][R#] = count
  const staffRequiredByRole = {};
  for (const e of inScopeEvents) {
    const eKey = idToEventKey[e.id];
    if (!eKey) continue;
    const rolesRequiredArr = Array.isArray(e?.extendedProps?.rolesRequired)
      ? e.extendedProps.rolesRequired
      : (Array.isArray(e?.rolesRequired) ? e.rolesRequired : []);
    const perRole = {};
    if (rolesRequiredArr.length) {
      for (const r of rolesRequiredArr) {
        const name = String(r?.role || '').trim();
        const cnt = Math.max(0, Number(r?.count) || 0);
        const rKey = roleNameToKey[name];
        if (rKey && cnt > 0) perRole[rKey] = (perRole[rKey] || 0) + cnt;
      }
    } else {
      // Fallback to default role with total staffRequired
      const rKey = roleNameToKey['default'];
      const cnt = Math.max(0, Number(staffRequiredMap[eKey] || 0));
      if (rKey && cnt > 0) perRole[rKey] = cnt;
    }
    staffRequiredByRole[eKey] = perRole;
  }

  // Eligible roles per user: { U# : [R#] }
  const eligibleRolesByUser = {};
  for (const [uKey, uid] of Object.entries(userKeyMap)) {
    const person = (people || []).find((p) => p.uid === uid) || {};
    const roles = rolesByUser.get(uid) || (Array.isArray(person.roles) ? person.roles : []);
    const rKeys = new Set();
    if (roles.length) {
      for (const rName of roles) {
        const rn = String(rName || '').trim();
        const rk = roleNameToKey[rn];
        if (rk) rKeys.add(rk);
      }
    } else if (usingDefaultRole && roleNameToKey['default']) {
      // Only in default role fallback case, everybody can take default
      rKeys.add(roleNameToKey['default']);
    }
    eligibleRolesByUser[uKey] = Array.from(rKeys);
  }

  // Series mappings: map recurring series to short keys and index events by series and role
  const seriesKeyMap = {};
  const eventSeriesKeyMap = {};
  const seriesRoleEvents = {}; // S# -> { R# -> [E#] }
  {
    const seriesIdSet = new Set();
    for (const e of inScopeEvents) {
      const sid = e?.extendedProps?.seriesId || e?.seriesId || null;
      if (sid) seriesIdSet.add(String(sid));
    }
    const seriesIdToKey = {};
    Array.from(seriesIdSet).forEach((sid, idx) => {
      const sKey = `S${idx + 1}`;
      seriesKeyMap[sKey] = sid;
      seriesIdToKey[sid] = sKey;
    });
    for (const e of inScopeEvents) {
      const eKey = idToEventKey[e.id];
      if (!eKey) continue;
      const sid = e?.extendedProps?.seriesId || e?.seriesId || null;
      const sKey = sid ? seriesIdToKey[String(sid)] : null;
      if (sKey) eventSeriesKeyMap[eKey] = sKey;
    }
    // Build per-series per-role event lists
    for (const [eKey, rolesObj] of Object.entries(staffRequiredByRole)) {
      const sKey = eventSeriesKeyMap[eKey] || null;
      if (!sKey) continue;
      const roleKeys = Object.keys(rolesObj || {});
      if (!roleKeys.length) continue;
      if (!seriesRoleEvents[sKey]) seriesRoleEvents[sKey] = {};
      for (const rKey of roleKeys) {
        if (!seriesRoleEvents[sKey][rKey]) seriesRoleEvents[sKey][rKey] = [];
        seriesRoleEvents[sKey][rKey].push(eKey);
      }
    }
  }

  // Decision variables: dictionary over all user × event × role triples
  const decisionVariables = {};
  for (const [eKey, rolesObj] of Object.entries(staffRequiredByRole)) {
    const roleKeys = Object.keys(rolesObj || {});
    if (!roleKeys.length) continue;
    for (const uKey of Object.keys(userKeyMap)) {
      const eligible = new Set(eligibleRolesByUser[uKey] || []);
      for (const rKey of roleKeys) {
        if (eligible.has(rKey)) {
          const name = `x_${uKey}_${eKey}_${rKey}`;
          decisionVariables[name] = { userKey: uKey, eventKey: eKey, roleKey: rKey };
        }
      }
    }
  }

  // Event-role slack decision variables: one per (event, role)
  const eventRoleDecisionVariables = Object.fromEntries(
    Object.entries(staffRequiredByRole).flatMap(([eKey, rolesObj]) =>
      Object.keys(rolesObj || {}).map((rKey) => [
        `d_${eKey}_${rKey}`,
        { eventKey: eKey, roleKey: rKey },
      ])
    )
  );

  // Series-distinct usage indicators: ys_{U}_{S}_{R}
  const seriesDistinctDecisionVariables = (() => {
    const out = {};
    const usersKeys = Object.keys(userKeyMap);
    for (const [sKey, rolesObj] of Object.entries(seriesRoleEvents || {})) {
      for (const rKey of Object.keys(rolesObj || {})) {
        for (const uKey of usersKeys) {
          const eligible = new Set(eligibleRolesByUser[uKey] || []);
          if (!eligible.has(rKey)) continue;
          const id = `ys_${uKey}_${sKey}_${rKey}`;
          out[id] = { userKey: uKey, seriesKey: sKey, roleKey: rKey };
        }
      }
    }
    return out;
  })();

  // Employment mapping per userKey and external set
  const employmentByUser = {};
  const externalUsers = [];
  for (const [uKey, uid] of Object.entries(userKeyMap)) {
    const contract = contractByUser.get(uid) || 'internal';
    employmentByUser[uKey] = contract;
    if (contract === 'external') externalUsers.push(uKey);
  }

  // Current assignees by (event, role) to support stability bonus
  const currentAssigneesByRole = {};
  {
    // Build inverse maps for quick lookup
    const uidToUserKey = Object.entries(userKeyMap).reduce((acc, [uk, uid]) => { acc[uid] = uk; return acc; }, {});
    for (const e of inScopeEvents) {
      const eKey = idToEventKey[e.id];
      if (!eKey) continue;
      const assignments = (e && (e.assignments || e?.extendedProps?.assignments)) || {};
      const perRole = {};
      // assignments shape: { roleNameOrId: [userIds] }
      for (const [roleNameOrId, uids] of Object.entries(assignments)) {
        const rName = String(roleNameOrId || '').trim();
        const rKey = roleNameToKey[rName];
        if (!rKey) continue;
        const arr = Array.isArray(uids) ? uids : [];
        const uKeys = arr.map((uid) => uidToUserKey[uid]).filter(Boolean);
        if (uKeys.length) perRole[rKey] = uKeys;
      }
      if (Object.keys(perRole).length) currentAssigneesByRole[eKey] = perRole;
    }
  }

  // External use indicator variables y_U for external users only
  const externalUseDecisionVariables = Object.fromEntries(
    externalUsers.map((uKey) => [`y_${uKey}`, { userKey: uKey }])
  );

  // External gating variables per (event, role)
  const externalGatingDecisionVariables = Object.fromEntries(
    Object.entries(staffRequiredByRole).flatMap(([eKey, rolesObj]) =>
      Object.keys(rolesObj || {}).map((rKey) => [
        `z_ext_${eKey}_${rKey}`,
        { eventKey: eKey, roleKey: rKey },
      ])
    )
  );

  // Adjacency break-pair decision variables p_{U}_{E_i}_{E_j} for each user and consecutive same-day pair
  const adjacencyPairDecisionVariables = (() => {
    const out = {};
    const usersKeys = Object.keys(userKeyMap);
    for (const edge of adjacencyEdges) {
      const { a, b } = edge || {};
      if (!a || !b) continue;
      for (const uKey of usersKeys) {
        const id = `p_${uKey}_${a}_${b}`;
        out[id] = { userKey: uKey, aEventKey: a, bEventKey: b };
      }
    }
    return out;
  })();

  // Day-work indicator variables w_{U}_{YYYY-MM-DD}
  const dayWorkDecisionVariables = {};
  for (const uKey of Object.keys(userKeyMap)) {
    for (const day of dayKeys) {
      const name = `w_${uKey}_${day}`;
      dayWorkDecisionVariables[name] = { userKey: uKey, dayKey: day };
    }
  }

  return {
    planningId,
    timeScope: { startDate: toYmd(start), endDate: toYmd(end) },
    participants,
    // Weekly default hours for contracts (used by constraints fallback)
    weeklyDefaults: {
      internal: Number(weeklyDefaults?.internal) || 40,
      external: Number(weeklyDefaults?.external) || 44,
    },
    optimizationIndexes: {
      users: userKeyMap,
      events: eventKeyMap,
      roles: roleKeyMap,
      staffRequired: staffRequiredMap,
      staffRequiredByRole,
      eventDuration: eventDurationMap,
      eventEffectiveDuration: eventEffectiveDurationMap,
      eventDurationMinutes: eventDurationMinutesMap,
      eventEffectiveMinutes: eventEffectiveMinutesMap,
      overlapsEdges,
      linkedEdges,
      dayKeys,
      userUnavailable,
      eventInSameWeek,
      eventDateYmd,
      eventStartMs: eventStartMsMap,
      eventEndMs: eventEndMsMap,
      eventLocation: eventLocationMap,
      decisionVariables,
      dayWorkDecisionVariables,
      eventRoleDecisionVariables,
      seriesDistinctDecisionVariables,
      employmentByUser,
      externalUsers,
      currentAssigneesByRole,
      seriesKeyMap,
      eventSeriesKeyMap,
      seriesRoleEvents,
      externalUseDecisionVariables,
      externalGatingDecisionVariables,
      eligibleRolesByUser,
      adjacencyEdges,
      adjacencyPairDecisionVariables,
    },
    version: 2,
  };
}
